import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiSearch, FiUser,
  FiChevronLeft,
} from 'react-icons/fi';
import DriverLicenseScanAmsFiles from './DriverLicenseScanAmsFiles';
import DriverLicenseScanPreview, { DLTransaction } from './DriverLicenseScanPreview';
import { AmsFileSelection } from '../coiscan/COIScanClientFiles';
import '../commissionscan/CommissionScan.css';
import './../../styles/QuoteProposal.css';
import '../../styles/DriverLicenseScanUpload.css';

type TabType = 'local' | 'client';

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME  = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];


const getToolboxToken  = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken') || '';

interface Client {
  name: string;
  databaseId: string;
  eMail?: string;
}

interface Props {
  onGoToHistory?: () => void;
}

const DriverLicenseScanUpload: React.FC<Props> = ({ onGoToHistory: _onGoToHistory }) => {
  /* Step */
  const [step, setStep] = useState<'client-select' | 'upload' | 'review'>('client-select');

  /* Client search */
  const [clientQuery,     setClientQuery]     = useState('');
  const [clientResults,   setClientResults]   = useState<Client[]>([]);
  const [clientLoading,   setClientLoading]   = useState(false);
  const [selectedClient,  setSelectedClient]  = useState<Client | null>(null);

  /* Upload */
  const [activeTab,        setActiveTab]        = useState<TabType>('local');
  const [file,             setFile]             = useState<File | null>(null);
  const [selectedAmsFile,  setSelectedAmsFile]  = useState<AmsFileSelection | null>(null);
  const [isDragging,       setIsDragging]       = useState(false);
  const [uploadLoading,    setUploadLoading]    = useState(false);
  const [uploadError,      setUploadError]      = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Balance */
  const [balance,             setBalance]             = useState<number>(0);
  const [balanceLoading,      setBalanceLoading]      = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  /* Review */
  const [transactionId,   setTransactionId]   = useState('');
  const [fileUrl,         setFileUrl]         = useState('');
  const [scanResponseRaw, setScanResponseRaw] = useState('');


  /* Balance fetch on mount */
  useEffect(() => {
    if (!(process.env.REACT_APP_STRIPEPAYMENTGATEWAY  || '')) return;
    setBalanceLoading(true);
    axios.get(`${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY  || '')}/api/billing/current-balance`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(res => {
        if (res.data.success) {
          const amt = res.data.currentBalance / 100;
          setBalance(amt);
          setInsufficientBalance(amt < 0.25);
        }
        setBalanceLoading(false);
      })
      .catch(() => setBalanceLoading(false));
  }, []);

  /* Client search debounce */
  useEffect(() => {
    if (!clientQuery.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI           || '')}/api/nowcert/customerlist`, {
        params: { searchText: clientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => { setClientResults(r.data || []); setClientLoading(false); })
        .catch(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientQuery]);

  const selectClient = useCallback((client: Client) => {
    if (client.databaseId !== selectedClient?.databaseId) {
      setFile(null);
      setSelectedAmsFile(null);
    }
    setSelectedClient(client);
    setClientQuery('');
    setClientResults([]);
    setStep('upload');
  }, [selectedClient]);

  /* File handling */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => ACCEPTED_MIME.includes(f.type) || /\.(pdf|jpg|jpeg|png)$/i.test(f.name),
    );
    if (first) {
      setFile(first);
      setUploadError('');
    } else {
      setUploadError('Please select a PDF, JPG, JPEG, or PNG file');
      setTimeout(() => setUploadError(''), 3000);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) pickFile(e.target.files);
    e.target.value = '';
  };

  const canScan = !uploadLoading && !insufficientBalance &&
    ((activeTab === 'local' && !!file) || (activeTab === 'client' && !!selectedAmsFile));

  /* Scan API */
  const onStartScan = useCallback(async () => {
    if (!selectedClient) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const data = new FormData();
      data.append('drivingLicense', activeTab === 'local' ? (file as File) : '');
      data.append('ncToken', getNcToken());
      data.append('insuredId', selectedClient.databaseId);
      if (activeTab === 'client' && selectedAmsFile) {
        data.append('amsFileIds', selectedAmsFile.file.id);
        data.append('folderId', selectedAmsFile.folderId);
      }
      data.append('isFileUploadFromAMS', String(activeTab === 'client'));
      data.append('selectedOption', JSON.stringify({
        useExistingInsured: true,
        insuredClientId:    selectedClient.databaseId,
        clientName:         selectedClient.name,
        folderId:           selectedAmsFile?.folderId || '',
        fileIds:            selectedAmsFile ? [selectedAmsFile.file.id] : [],
      }));

      const res = await axios.post(`${(process.env.REACT_APP_PolicyAgent_Url      || '')}/scan_driving_license`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      if (res.data.success === true) {
        const raw = JSON.parse(res.data.drivingLicenseResponse);
        setTransactionId(raw.transactionId || '');
        setFileUrl(raw.fileUrl || '');
        setScanResponseRaw(res.data.drivingLicenseResponse || '');
        setStep('review');
      } else {
        setUploadError(res.data.errorMessage || 'Scan failed. Please try again.');
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.errorMessage || 'Error while scanning document.');
    } finally {
      setUploadLoading(false);
    }
  }, [activeTab, file, selectedAmsFile, selectedClient]);


  const resetAll = useCallback(() => {
    setStep('client-select');
    setClientQuery('');
    setClientResults([]);
    setSelectedClient(null);
    setActiveTab('local');
    setFile(null);
    setSelectedAmsFile(null);
    setIsDragging(false);
    setUploadLoading(false);
    setUploadError('');
    setTransactionId(''); setFileUrl(''); setScanResponseRaw('');
  }, []);

  /* ────────────────────────────────────────────────
     STEP 1: Client Select
  ──────────────────────────────────────────────── */
  if (step === 'client-select') {
    return (
      <div className="cs-main driver-license-scan-upload__main-flush">
        <div className="cs-client-search">
          <div className="cs-client-search__input-wrap">
            <FiSearch size={15} className="cs-client-search__icon" />
            <input
              className="cs-client-search__input"
              type="text"
              placeholder="Enter client name"
              value={clientQuery}
              onChange={e => { setClientQuery(e.target.value); if (selectedClient) setSelectedClient(null); }}
              autoFocus
            />
            {clientLoading && <span className="cs-client-search__spinner" />}
          </div>
          {clientResults.length > 0 && (
            <ul className="cs-client-dropdown">
              {clientResults.map(c => (
                <li
                  key={c.databaseId}
                  className="cs-client-dropdown__item"
                  onClick={() => selectClient(c)}
                >
                  <div className="driver-license-scan-upload__dropdown-col">
                    <span className="cs-client-dropdown__name">{c.name}</span>
                    {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {clientQuery.trim() && !clientLoading && clientResults.length === 0 && (
            <ul className="cs-client-dropdown">
              <li className="cs-client-dropdown__item driver-license-scan-upload__no-results">
                No clients found
              </li>
            </ul>
          )}
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────────
     STEP 2: Upload
  ──────────────────────────────────────────────── */
  if (step === 'upload') {
    return (
      <div className="cs-main driver-license-scan-upload__main-flush">
        {/* Top row */}
        <div className="driver-license-scan-upload__top-row">
          <button
            className="cs-btn-cancel driver-license-scan-upload__back-btn"
            onClick={() => { setStep('client-select'); setClientQuery(selectedClient?.name || ''); setClientResults([]); }}
          >
            <FiChevronLeft size={13} />
            Back
          </button>
          <span className="driver-license-scan-upload__client-label">
            Client: {selectedClient?.name}
          </span>
        </div>

        {/* Balance bar */}
        {/* <div className="coi-balance-bar">
          {balanceLoading ? (
            <span className="cs-upload-spinner" />
          ) : (
            <>
              <span>Balance: ${balance.toFixed(2)}</span>
              <span style={{ opacity: 0.6 }}>· 25¢ per scan</span>
            </>
          )}
        </div> */}

        {/* Insufficient balance banner */}
        {/* {insufficientBalance && (
          <div className="coi-insufficient-banner">
            <FiAlertCircle size={14} />
            Insufficient balance. Please add credits to continue scanning.
          </div>
        )} */}

        {/* Tabs */}
        <div className="cs-tabs">
          <button
            className={`cs-tab${activeTab === 'local' ? ' cs-tab--active' : ''}`}
            onClick={() => { setActiveTab('local'); setSelectedAmsFile(null); }}
          >
            Select Document from Local Drive
          </button>
          <button
            className={`cs-tab${activeTab === 'client' ? ' cs-tab--active' : ''}`}
            onClick={() => { setActiveTab('client'); setFile(null); }}
          >
            Select Document from Client Profile
          </button>
        </div>

        {/* Content */}
        <div className="cs-content">
          {activeTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              {!file ? (
                <>
                  <div className="cs-dropzone__icon">
                    <FiFileText size={28} />
                  </div>
                  <p className="cs-dropzone__title">Drag & Drop your file here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button
                    className="cs-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">Allowed: PDF, JPG, JPEG, PNG</p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon">
                    <FiFileText size={20} />
                  </div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button
                    className="cs-file-card__delete"
                    onClick={() => setFile(null)}
                    title="Remove file"
                  >
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                className="driver-license-scan-upload__hidden-input"
                onChange={handleFileInput}
              />
            </div>
          ) : (
            <div className="cs-client-panel cs-client-panel--browser">
              <DriverLicenseScanAmsFiles
                insuredId={selectedClient!.databaseId}
                clientName={selectedClient?.name}
                onFileSelected={setSelectedAmsFile}
              />
            </div>
          )}
        </div>

        {/* Info banner */}
        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>
            This tool scans driver licenses. Supported: <strong>PDF, JPG, JPEG, PNG</strong>
          </span>
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="driver-license-scan-upload__upload-error">{uploadError}</div>
        )}

        {/* Footer */}
        <div className="cs-footer">
          <span className="cs-footer__status">
            {activeTab === 'local'
              ? (!file ? 'No file added yet.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)
            }
          </span>
          <div className="cs-footer__actions">
            <button
              className="cs-btn-cancel"
              onClick={resetAll}
              disabled={uploadLoading}
            >
              Cancel
            </button>
            <motion.button
              className="cs-btn-start"
              disabled={!canScan}
              whileHover={{ scale: canScan ? 1.03 : 1 }}
              whileTap={{ scale: canScan ? 0.97 : 1 }}
              onClick={onStartScan}
            >
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────────
     STEP 3: Review — reuses DriverLicenseScanPreview
  ──────────────────────────────────────────────── */
  const reviewTx: DLTransaction = {
    transactionId,
    fileName: activeTab === 'local'
      ? (file?.name || '')
      : (selectedAmsFile?.file.name || ''),
    created:  new Date().toISOString(),
    userName: '',
    status:   'Success',
    responseJson:        scanResponseRaw,
    selectionOptionsJson: JSON.stringify({
      clientName:      selectedClient?.name       || '',
      insuredClientId: selectedClient?.databaseId || '',
    }),
    isUpdateInNowcerts: false,
  };

  return (
    <div className="driver-license-scan-upload__review-wrap">
      <DriverLicenseScanPreview
        tx={reviewTx}
        inline
        initialPdfUrl={fileUrl}
        closeLabel="Scan Again"
        onClose={resetAll}
      />
    </div>
  );
};

export default DriverLicenseScanUpload;

